import { Suspense, lazy, useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { Toaster } from 'react-hot-toast'
import { Navbar } from '@/components/layout/Navbar'
import { Footer } from '@/components/layout/Footer'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { CartDrawer } from '@/components/cart/CartDrawer'
import { ChatWidget } from '@/components/chat/ChatWidget'
import { PromoBanner } from '@/components/ui/PromoBanner'
import { Skeleton } from '@/components/ui/Skeleton'
import { ROUTES } from '@/constants'
import { pageView } from '@/utils/facebookPixel'

const HomePage = lazy(() => import('@/pages/HomePage'))
const ShopPage = lazy(() => import('@/pages/ShopPage'))
const ProductPage = lazy(() => import('@/pages/ProductPage'))
const CartPage = lazy(() => import('@/pages/CartPage'))
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'))
const OrderConfirmationPage = lazy(() => import('@/pages/OrderConfirmationPage'))
const TrackOrderPage = lazy(() => import('@/pages/TrackOrderPage'))
const WishlistPage = lazy(() => import('@/pages/WishlistPage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const ReturnPolicyPage = lazy(() => import('@/pages/ReturnPolicyPage'))
const ContactPage = lazy(() => import('@/pages/ContactPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Admin is lazy-loaded as its own chunk so none of it reaches a shopper's
// initial bundle. Keep these imports lazy.
const AdminLayout = lazy(() =>
  import('@/components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout }))
)
const RequireAdmin = lazy(() =>
  import('@/components/admin/RequireAdmin').then((m) => ({ default: m.RequireAdmin }))
)
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'))
const AdminProductsPage = lazy(() => import('@/pages/AdminProductsPage'))
const AdminOrdersPage = lazy(() => import('@/pages/AdminOrdersPage'))
const AdminOrderDetailPage = lazy(() => import('@/pages/AdminOrderDetailPage'))
const AdminInventoryPage = lazy(() => import('@/pages/AdminInventoryPage'))
const AdminCategoriesPage = lazy(() => import('@/pages/AdminCategoriesPage'))
const AdminCustomersPage = lazy(() => import('@/pages/AdminCustomersPage'))
const AdminSettingsPage = lazy(() => import('@/pages/AdminSettingsPage'))
const AdminReviewsPage = lazy(() => import('@/pages/AdminReviewsPage'))
const AdminBannersPage = lazy(() => import('@/pages/AdminBannersPage'))
const AdminSubscribersPage = lazy(() => import('@/pages/AdminSubscribersPage'))

// The packing dashboard is its own chunk for the same reason: a packer's
// login must not pull the admin bundle, and a shopper's must not pull either.
const PackerLayout = lazy(() =>
  import('@/components/packer/PackerLayout').then((m) => ({ default: m.PackerLayout }))
)
const RequirePacker = lazy(() =>
  import('@/components/packer/RequirePacker').then((m) => ({ default: m.RequirePacker }))
)
const PackerQueuePage = lazy(() => import('@/pages/PackerQueuePage'))
const PackerOrderPage = lazy(() => import('@/pages/PackerOrderPage'))

function RouteTracker() {
  const location = useLocation()
  useEffect(() => {
    pageView()
  }, [location.pathname])
  return null
}

function PageFallback() {
  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-12 sm:px-6 lg:px-8">
      <Skeleton className="h-10 w-1/3" />
      <Skeleton className="h-64 w-full" />
      <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full" />
        ))}
      </div>
    </div>
  )
}

function ChromeFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Skeleton className="h-10 w-40" />
    </div>
  )
}

export default function App() {
  const location = useLocation()
  const isAdminRoute =
    location.pathname === ROUTES.adminDashboard ||
    location.pathname.startsWith(`${ROUTES.adminDashboard}/`)
  const isPackerRoute =
    location.pathname === ROUTES.packer || location.pathname.startsWith(`${ROUTES.packer}/`)

  // Chrome (storefront or admin) is rendered *outside* <Routes> on purpose:
  // the pathname key below remounts the entire subtree on every navigation to
  // drive the page transition, and a navbar that remounts with it loses its
  // state and re-animates on every click.
  const pages = (
    <Suspense fallback={<PageFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {isAdminRoute ? (
            <>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/orders" element={<AdminOrdersPage />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetailPage />} />
              <Route path="/admin/products" element={<AdminProductsPage />} />
              <Route path="/admin/inventory" element={<AdminInventoryPage />} />
              <Route path="/admin/categories" element={<AdminCategoriesPage />} />
              <Route path="/admin/customers" element={<AdminCustomersPage />} />
              <Route path="/admin/reviews" element={<AdminReviewsPage />} />
              <Route path="/admin/banners" element={<AdminBannersPage />} />
              <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </>
          ) : isPackerRoute ? (
            <>
              <Route path="/packer" element={<PackerQueuePage />} />
              <Route path="/packer/orders/:id" element={<PackerOrderPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </>
          ) : (
            <>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order/:orderNumber" element={<OrderConfirmationPage />} />
              <Route path="/track" element={<TrackOrderPage />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/return-policy" element={<ReturnPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </>
          )}
        </Routes>
      </AnimatePresence>
    </Suspense>
  )

  return (
    <>
      <ScrollToTop />
      <RouteTracker />
      {isAdminRoute ? (
        <Suspense fallback={<ChromeFallback />}>
          <RequireAdmin>
            <AdminLayout>{pages}</AdminLayout>
          </RequireAdmin>
        </Suspense>
      ) : isPackerRoute ? (
        <Suspense fallback={<ChromeFallback />}>
          <RequirePacker>
            <PackerLayout>{pages}</PackerLayout>
          </RequirePacker>
        </Suspense>
      ) : (
        <>
          <PromoBanner />
          <Navbar />
          {/* Phones: room under the page for the fixed BottomNav. */}
          <div className="storefront-shell pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-0">
            {pages}
            <Footer />
          </div>
          <CartDrawer />
          <ChatWidget />
        </>
      )}
      <Toaster
        position="top-center"
        toastOptions={{
          style: { borderRadius: 9999, padding: '10px 18px', fontSize: 14 },
          success: { iconTheme: { primary: '#FF5A5F', secondary: '#fff' } },
        }}
      />
    </>
  )
}
